import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle, Info, X, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { Toast, ToastType } from "@/stores/toast-store";
import { useToastStore } from "@/stores/toast-store";

const iconMap: Record<ToastType, typeof CheckCircle> = {
	success: CheckCircle,
	info: Info,
	warning: AlertTriangle,
	error: XCircle,
};

const styleMap: Record<ToastType, string> = {
	success: "border-l-success text-success",
	info: "border-l-info text-info",
	warning: "border-l-warning text-warning",
	error: "border-l-error text-error",
};

function ToastItem({ toast }: { toast: Toast }) {
	const removeToast = useToastStore((state) => state.removeToast);
	const [isExiting, setIsExiting] = useState(false);

	const dismiss = useCallback(() => {
		setIsExiting(true);
		// Animation abwarten, dann entfernen
		const timer = setTimeout(() => removeToast(toast.id), 200);
		return () => clearTimeout(timer);
	}, [toast.id, removeToast]);

	useEffect(() => {
		if (toast.duration <= 0) {
			return;
		}

		const timer = setTimeout(() => {
			dismiss();
		}, toast.duration);

		return () => clearTimeout(timer);
	}, [toast.duration, dismiss]);

	const Icon = iconMap[toast.type];

	return (
		<div
			role="alert"
			className={`
				flex items-start gap-3 w-80 p-4
				bg-surface shadow-lg rounded-lg border-l-4
				${styleMap[toast.type]}
				${isExiting ? "animate-toast-out" : "animate-toast-in"}
			`}
		>
			{toast.image ? (
				<img src={toast.image} alt="" className="size-10 rounded-full object-cover shrink-0" />
			) : (
				<Icon size={20} className="shrink-0 mt-0.5" />
			)}
			<div className="flex-1 min-w-0">
				{toast.title && <p className="font-medium text-text text-base">{toast.title}</p>}
				<p className="text-base text-text-secondary">{toast.message}</p>
				{toast.link && (
					<Link
						to={toast.link}
						onClick={dismiss}
						className="text-sm text-primary font-medium hover:underline mt-1 inline-block"
					>
						Jetzt antworten
					</Link>
				)}
			</div>
			{toast.dismissible && (
				<button
					type="button"
					onClick={dismiss}
					className="shrink-0 min-h-11 min-w-11 flex items-center justify-center rounded text-text-muted hover:text-text transition-colors cursor-pointer"
					aria-label="Schließen"
				>
					<X size={16} />
				</button>
			)}
		</div>
	);
}

export function ToastContainer() {
	const toasts = useToastStore((state) => state.toasts);

	if (toasts.length === 0) {
		return null;
	}

	return (
		<div
			className="fixed top-4 right-4 flex flex-col gap-2"
			style={{ zIndex: "var(--z-index-toast)" }}
			aria-live="polite"
		>
			{toasts.map((toast) => (
				<ToastItem key={toast.id} toast={toast} />
			))}
		</div>
	);
}
